import React, { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

const STORAGE_KEY = "theme";

/**
 * ThemeToggle
 *
 * Dark mode is the DEFAULT. Light is opt-in (saved as "light" in localStorage).
 *
 * Two render variants:
 *   <ThemeToggle />              -> inline pill button (use inside Settings)
 *   <ThemeToggle floating />     -> legacy floating FAB (kept for back-compat,
 *                                   should not be used in main UI any more)
 *
 * The component reads the live `data-theme` attribute on <html> so it stays in
 * sync with any other place that toggles the theme (e.g. the pre-React inline
 * script in index.html, or a future settings-page toggle).
 */
export default function ThemeToggle({ floating = false }) {
  const readDocTheme = () =>
    document.documentElement.getAttribute("data-theme") === "dark";

  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      // Default to dark. Only switch off if the user explicitly chose light.
      if (saved === "light") return false;
      return true;
    } catch {
      return true;
    }
  });

  const [hovered, setHovered] = useState(false);

  // Apply or remove data-theme="dark" on <html> whenever the state changes.
  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.setAttribute("data-theme", "dark");
      try { localStorage.setItem(STORAGE_KEY, "dark"); } catch {}
    } else {
      root.removeAttribute("data-theme");
      try { localStorage.setItem(STORAGE_KEY, "light"); } catch {}
    }
  }, [dark]);

  // Re-sync if another part of the app changes data-theme directly.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const live = readDocTheme();
      if (live !== dark) setDark(live);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, [dark]);

  const baseStyle = {
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition:
      "background 0.22s ease, box-shadow 0.22s ease, transform 0.15s ease",
    color: "#ffffff",
  };

  if (floating) {
    return (
      <button
        type="button"
        aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
        onClick={() => setDark((d) => !d)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          ...baseStyle,
          position: "fixed",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
          right: "20px",
          zIndex: 9999,
          width: "44px",
          height: "44px",
          borderRadius: "50%",
          background: dark ? "var(--primary)" : "var(--dark)",
          boxShadow: dark
            ? "0 4px 16px rgba(255, 138, 0, 0.45), 0 2px 6px rgba(0,0,0,0.25)"
            : "0 4px 16px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2)",
          transform: hovered ? "scale(1.08)" : "scale(1)",
        }}
      >
        {dark ? <Sun size={18} /> : <Moon size={18} />}
      </button>
    );
  }

  // Inline pill -- designed to sit inside a Settings row.
  return (
    <button
      type="button"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      onClick={() => setDark((d) => !d)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...baseStyle,
        gap: "8px",
        padding: "9px 14px",
        borderRadius: "999px",
        background: dark ? "var(--primary)" : "var(--dark)",
        boxShadow: hovered
          ? "0 6px 18px rgba(255, 138, 0, 0.35)"
          : "0 2px 6px rgba(0,0,0,0.18)",
        transform: hovered ? "translateY(-1px)" : "translateY(0)",
        fontSize: "0.85rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
      }}
    >
      {dark ? <Sun size={16} /> : <Moon size={16} />}
      <span>{dark ? "Light mode" : "Dark mode"}</span>
    </button>
  );
}
